import { css, cx } from '@emotion/css';
import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { useOverlay } from '@react-aria/overlays';
import { useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { createAssistantContextItem, useAssistant } from '@grafana/assistant';
import { type GrafanaTheme2 } from '@grafana/data';
import { t, Trans } from '@grafana/i18n';
import { config } from '@grafana/runtime';
import {
  Badge,
  Box,
  Button,
  Dropdown,
  Icon,
  IconButton,
  Menu,
  Stack,
  TagList,
  Text,
  TextLink,
  Tooltip,
  useStyles2,
} from '@grafana/ui';
import { ConfirmContent } from '@grafana/ui/internal';
import { attachSkeleton, type SkeletonComponent } from '@grafana/ui/unstable';
import { type PluginDashboard } from 'app/types/plugins';

import { CompatibilityBadge, type CompatibilityState } from './CompatibilityBadge';
import { NewTemplateDashboardInteractions } from './analytics/main';
import { CONTENT_KINDS } from './constants';
import { type GnetDashboard } from './types';
import { buildAssistantPrompt, buildTemplateContextData, buildTemplateContextTitle } from './utils/assistantHelpers';

interface Details {
  id: string;
  datasource: string;
  dependencies: string[];
  publishedBy: string;
  lastUpdate: string;
  grafanaComUrl?: string;
}

/** Lightweight dashboard shape used by custom-template cards. */
export interface CustomTemplateDashboard {
  id: string;
  name: string;
  description: string;
}

interface BaseProps {
  title: string;
  imageUrl?: string;
  details?: Details;
  onClick: (customizeWithAssistant?: boolean) => void;
  onClose?: () => void;
  isLogo?: boolean; // Indicates if imageUrl is a small logo vs full screenshot
  showDatasourceProvidedBadge?: boolean;
  showCommunityBadge?: boolean;
  dimThumbnail?: boolean; // Apply 50% opacity to thumbnail when badge is shown
  /** Show the compact compatibility badge (replaces showCompatibilityButton) */
  showCompatibilityBadge?: boolean;
  /** State for the compatibility badge (idle, loading, success, error) */
  compatibilityState?: CompatibilityState;
  /** Handler called when Check button is clicked in the badge */
  onCompatibilityCheck?: () => void;
  /** Whether to show the "Customize with assistant" button (caller must check relevant feature flags) */
  showAssistantButton?: boolean;
  onEdit?: () => void;
  /**
   * Handler called when the user confirms deleting the template.
   */
  onDelete?: () => void | Promise<void>;
  /** Optional tags to display at the bottom of the card */
  tags?: string[];
  /** Resolved display name of the user who created the template. Only rendered for `custom_dashboard_template`. */
  createdByName?: string;
}

// Discriminated on `kind` so the `dashboard` shape is correlated with the kind of card being rendered.
type Props = BaseProps &
  (
    | { kind: 'template_dashboard' | 'suggested_dashboard'; dashboard: PluginDashboard | GnetDashboard }
    | { kind: 'custom_dashboard_template'; dashboard: CustomTemplateDashboard }
  );

function DashboardCardComponent({
  title,
  imageUrl,
  onClick,
  onClose,
  dashboard,
  details,
  isLogo,
  showDatasourceProvidedBadge,
  showCommunityBadge,
  dimThumbnail,
  kind,
  showCompatibilityBadge,
  compatibilityState,
  onCompatibilityCheck,
  showAssistantButton,
  onEdit,
  onDelete,
  tags,
  createdByName,
}: Props) {
  const styles = useStyles2(getStyles);
  const isCompatibilityAppEnabled = config.featureToggles.dashboardValidatorApp;
  const isCustomTemplate = kind === 'custom_dashboard_template';
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const onConfirmDelete = async () => {
    if (!onDelete) {
      return;
    }
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteConfirm(false);
    } finally {
      setIsDeleting(false);
    }
  };

  // Edit and Delete live in a kebab menu on custom-template cards only.
  const hasCardMenu = isCustomTemplate && (Boolean(onEdit) || Boolean(onDelete));

  const detailsButton = details && (
    <Tooltip interactive={true} content={<DetailsTooltipContent details={details} />} placement="right">
      <IconButton name="info-circle" size="md" aria-label={t('dashboard-library.card.details-tooltip', 'Details')} />
    </Tooltip>
  );

  const { isAvailable: assistantAvailable, openAssistant } = useAssistant();

  const assistantDashboard: PluginDashboard | GnetDashboard | null =
    kind === 'custom_dashboard_template' ? null : dashboard;
  const assistantKind: 'template_dashboard' | 'suggested_dashboard' | null =
    kind === 'custom_dashboard_template' ? null : kind;

  const onUseAssistantClick = () => {
    if (!assistantAvailable || !assistantDashboard || !assistantKind) {
      return;
    }
    const templateContext = createAssistantContextItem('structured', {
      hidden: false,
      title: buildTemplateContextTitle(assistantDashboard, assistantKind),
      data: buildTemplateContextData(assistantDashboard, assistantKind),
    });
    openAssistant?.({
      origin: 'dashboard-library/use-dashboard',
      mode: 'dashboarding',
      prompt: buildAssistantPrompt(assistantKind),
      context: [templateContext],
      autoSend: true,
    });
    // these both close the modal and redirect the user to the template dashboard url
    onClose?.();
    onClick?.(true);
  };

  const hasCompatActions = isCompatibilityAppEnabled && showCompatibilityBadge && onCompatibilityCheck;

  return (
    <article className={styles.card}>
      {hasCardMenu && (
        <div className={styles.cardMenu}>
          <Dropdown
            placement="bottom-end"
            onVisibleChange={(visible) =>
              visible &&
              NewTemplateDashboardInteractions.dropdownMenuClicked({
                contentKind: CONTENT_KINDS.CUSTOM_DASHBOARD_TEMPLATE,
              })
            }
            overlay={
              <Menu>
                {onEdit && (
                  <Menu.Item
                    label={t('dashboard-library.card.edit-template-menu-item', 'Edit')}
                    icon="pen"
                    ariaLabel={t('dashboard-library.card.edit-template-button-label', 'Edit template: {{title}}', {
                      title,
                    })}
                    onClick={onEdit}
                  />
                )}
                {onDelete && (
                  <Menu.Item
                    destructive
                    label={t('dashboard-library.card.delete-template-menu-item', 'Delete')}
                    icon="trash-alt"
                    ariaLabel={t('dashboard-library.card.delete-template-button-label', 'Delete template: {{title}}', {
                      title,
                    })}
                    onClick={() => setShowDeleteConfirm(true)}
                  />
                )}
              </Menu>
            }
          >
            <IconButton
              name="ellipsis-v"
              aria-label={t('dashboard-library.card.more-actions-label', 'More actions for {{title}}', { title })}
            />
          </Dropdown>
        </div>
      )}
      {!isCustomTemplate && (
        <h3 className={styles.title}>
          <span className={styles.titleWithInfo} role="group" aria-label={title}>
            <span className={styles.titleText}>{title}</span>
            {detailsButton}
          </span>
        </h3>
      )}
      <div
        className={cx(
          isLogo ? styles.logoContainer : styles.thumbnailContainer,
          isCustomTemplate && styles.customTemplateThumbnail
        )}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            className={cx(
              isLogo ? styles.logo : styles.thumbnail,
              dimThumbnail && showDatasourceProvidedBadge && styles.dimmedImage,
              kind === 'suggested_dashboard' ? styles.thumbnailCoverImage : styles.thumbnailContainImage
            )}
            onError={(e) => {
              console.error('Failed to load image for:', title, 'URL:', imageUrl);
              e.currentTarget.style.display = 'none';
            }}
          />
        ) : (
          <div className={styles.noImage}>
            <Trans i18nKey="dashboard-library.card.no-preview">No preview available </Trans>
          </div>
        )}
        {showDatasourceProvidedBadge && (
          <div className={styles.badgeContainer}>
            <Badge
              text={t('dashboard-library.card.datasource-provided-badge', 'Data source provided')}
              color="orange"
            />
          </div>
        )}
        {showCommunityBadge && (
          <div className={styles.badgeContainer}>
            <Badge text={t('dashboard-library.card.community-badge', 'Community')} color="blue" />
          </div>
        )}
        <div className={styles.thumbnailOverlay}>
          <Button
            variant="secondary"
            className={styles.overlayButton}
            onClick={() => onClick()}
            aria-label={
              kind === 'template_dashboard'
                ? t('dashboard-library.card.view-template-button-label', 'View template: {{title}}', { title })
                : kind === 'suggested_dashboard'
                  ? t('dashboard-library.card.view-dashboard-button-label', 'View dashboard: {{title}}', { title })
                  : t('dashboard-library.card.use-template-button-label', 'Use template: {{title}}', { title })
            }
          >
            {kind === 'template_dashboard' ? (
              <Trans i18nKey="dashboard-library.card.view-template-button">View template</Trans>
            ) : kind === 'suggested_dashboard' ? (
              <Trans i18nKey="dashboard-library.card.view-dashboard-button">View dashboard</Trans>
            ) : (
              <Trans i18nKey="dashboard-library.card.use-template-button">Use template</Trans>
            )}
          </Button>
          {assistantAvailable && showAssistantButton && (
            <Button
              variant="secondary"
              className={styles.overlayButton}
              onClick={onUseAssistantClick}
              icon="ai-sparkle"
              aria-label={t(
                'dashboard-library.card.customize-with-assistant-button-label',
                'Customize with Assistant: {{title}}',
                { title }
              )}
            >
              <Trans i18nKey="dashboard-library.card.customize-with-assistant-button">Customize with Assistant</Trans>
            </Button>
          )}
        </div>
      </div>
      <div className={styles.bottomSection}>
        {isCustomTemplate && (
          <h3 className={styles.customTemplateTitle}>
            <span className={styles.titleText}>{title}</span>
          </h3>
        )}
        {dashboard.description && (
          <div title={dashboard.description}>
            <p data-testid="dashboard-card-description" className={styles.description}>
              {dashboard.description}
            </p>
          </div>
        )}
        {tags && tags.length > 0 && (
          <div className={styles.tagsContainer}>
            <TagList tags={tags} className={styles.tagList} />
          </div>
        )}
        {isCustomTemplate && createdByName && (
          <p className={styles.createdBy}>
            <span className={styles.createdByLabel}>
              <Trans i18nKey="dashboard-library.dashboard-card.created-by-label">Created by:</Trans>
            </span>{' '}
            {createdByName}
          </p>
        )}
        {hasCompatActions && (
          <div className={styles.actionsContainer}>
            {isCompatibilityAppEnabled && showCompatibilityBadge && onCompatibilityCheck && (
              <CompatibilityBadge
                state={compatibilityState ?? { status: 'idle' }}
                onCheck={onCompatibilityCheck}
                onRetry={onCompatibilityCheck}
              />
            )}
          </div>
        )}
      </div>
      {showDeleteConfirm && (
        <DeleteTemplateConfirm
          title={title}
          isDeleting={isDeleting}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={onConfirmDelete}
        />
      )}
    </article>
  );
}

interface DeleteTemplateConfirmProps {
  title: string;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Inline delete confirmation rendered as an opaque overlay covering the card.
 */
function DeleteTemplateConfirm({ title, isDeleting, onCancel, onConfirm }: DeleteTemplateConfirmProps) {
  const styles = useStyles2(getStyles);
  const ref = useRef<HTMLDivElement>(null);
  const { dialogProps } = useDialog({ role: 'alertdialog' }, ref);
  const { overlayProps } = useOverlay(
    { isOpen: true, onClose: onCancel, isDismissable: false, isKeyboardDismissDisabled: isDeleting },
    ref
  );

  return (
    <FocusScope contain restoreFocus>
      <div
        ref={ref}
        className={styles.confirmOverlay}
        aria-label={t('dashboard-library.card.delete-confirm-title', 'Delete this template?')}
        {...overlayProps}
        {...dialogProps}
      >
        <ConfirmContent
          body={
            <Stack direction="column" gap={1}>
              <Stack direction="row" alignItems="center" gap={1}>
                <Icon name="exclamation-triangle" size="lg" className={styles.confirmIcon} />
                <Text element="h3" variant="h5">
                  <Trans i18nKey="dashboard-library.card.delete-confirm-title">Delete this template?</Trans>
                </Text>
              </Stack>
              <Text color="secondary" variant="bodySmall">
                {t(
                  'dashboard-library.card.delete-confirm-body',
                  'This permanently deletes "{{templateTitle}}". This action cannot be undone.',
                  { templateTitle: title }
                )}
              </Text>
            </Stack>
          }
          confirmPromptText={t('dashboard-library.card.delete-confirm-prompt', 'Delete')}
          confirmButtonLabel={t('dashboard-library.card.delete-confirm-confirm', 'Delete')}
          confirmButtonVariant="destructive"
          dismissButtonLabel={t('dashboard-library.card.delete-confirm-cancel', 'Cancel')}
          dismissButtonVariant="secondary"
          onConfirm={onConfirm}
          onDismiss={onCancel}
        />
      </div>
    </FocusScope>
  );
}

function DetailsTooltipContent({ details }: { details: Details }) {
  const Section = ({ label, value }: { label: string; value: string }) => {
    return (
      <Box display="flex" direction="column" gap={1}>
        <Text element="p">{label}</Text>
        <Text element="p" color="secondary">
          {value}
        </Text>
      </Box>
    );
  };

  return (
    <Box display="flex">
      <Box display="flex" direction="column" gap={1} width={{ xs: 'auto', md: 340 }}>
        <Section label={t('dashboard-library.dashboard-card.details.id', 'ID')} value={details.id} />
        <Section
          label={t('dashboard-library.dashboard-card.details.datasource', 'Datasource')}
          value={details.datasource}
        />
        <Section
          label={t('dashboard-library.dashboard-card.details.dependencies', 'Dependencies')}
          value={details.dependencies.join(' | ')}
        />
        <Section
          label={t('dashboard-library.dashboard-card.details.published-by', 'Published By')}
          value={details.publishedBy}
        />
        <Section
          label={t('dashboard-library.dashboard-card.details.last-update', 'Last Update')}
          value={details.lastUpdate}
        />
        {details.grafanaComUrl && (
          <Box display="flex" direction="column" gap={1}>
            <TextLink href={details.grafanaComUrl} external>
              {t('dashboard-library.dashboard-card.details.view-on-grafana-com', 'View on Grafana.com')}
            </TextLink>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function getStyles(theme: GrafanaTheme2) {
  const thumbnailOverlay = css({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    opacity: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    [theme.transitions.handleMotion('no-preference', 'reduce')]: {
      transition: 'opacity 0.15s ease',
    },
  });

  return {
    card: css({
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '350px',
      height: '100%',
      background: 'transparent',
      border: `1px solid ${theme.colors.border.strong}`,
      borderRadius: theme.shape.radius.default,
      overflow: 'hidden',
      padding: theme.spacing(1),
      [theme.transitions.handleMotion('no-preference', 'reduce')]: {
        transition: theme.transitions.create(['background-color', 'border-color'], {
          duration: theme.transitions.duration.short,
        }),
      },
    }),
    cardMenu: css({
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(1.5),
    }),
    confirmOverlay: css({
      position: 'absolute',
      inset: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      // Opaque background hides the underlying card content without changing the card's height.
      backgroundColor: theme.colors.background.primary,
      borderRadius: theme.shape.radius.default,
      padding: theme.spacing(2),
      overflowY: 'auto',
    }),
    confirmIcon: css({
      color: theme.colors.warning.text,
    }),
    thumbnailContainer: css({
      // Render visually above the heading while keeping heading first in source order for tab/keyboard order.
      order: -1,
      marginBottom: theme.spacing(1),
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.shape.radius.default,
      width: '100%',
      aspectRatio: '16/9',
      backgroundColor: theme.colors.background.canvas,
      position: 'relative',
      [`&:hover .${thumbnailOverlay}, &:focus-within .${thumbnailOverlay}`]: {
        opacity: 1,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '100%',
        background: 'linear-gradient(to bottom, rgba(6, 6, 6, 0) 50%, #060606 100%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
    }),
    thumbnailOverlay,
    overlayButton: css({
      width: '80%',
      justifyContent: 'center',
    }),
    thumbnail: css({
      width: '100%',
      height: '100%',
    }),
    thumbnailCoverImage: css({
      objectFit: 'cover',
    }),
    thumbnailContainImage: css({
      objectFit: 'contain',
    }),
    logoContainer: css({
      order: -1,
      marginBottom: theme.spacing(1),
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: theme.shape.radius.default,
      width: '100%',
      aspectRatio: '16/9',
      backgroundColor: theme.colors.background.secondary,
      position: 'relative',
      [`&:hover .${thumbnailOverlay}, &:focus-within .${thumbnailOverlay}`]: {
        opacity: 1,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: '50%',
        background: 'linear-gradient(to bottom, rgba(6, 6, 6, 0) 26%, #060606 100%)',
        pointerEvents: 'none',
        zIndex: 0,
      },
    }),
    logo: css({
      objectFit: 'fill',
    }),
    noImage: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.colors.text.secondary,
      fontSize: theme.typography.bodySmall.fontSize,
      height: '100%',
      width: '100%',
    }),
    customTemplateThumbnail: css({
      '&::after': {
        content: 'none',
      },
      [`& .${thumbnailOverlay}`]: {
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
      },
    }),
    customTemplateTitle: css({
      margin: 0,
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: theme.typography.h6.lineHeight,
      letterSpacing: 'inherit',
      color: theme.colors.text.primary,
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '100%',
    }),
    createdBy: css({
      margin: 0,
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.colors.text.secondary,
    }),
    createdByLabel: css({
      fontWeight: theme.typography.fontWeightBold,
    }),
    bottomSection: css({
      display: 'flex',
      flexDirection: 'column',
      wordBreak: 'break-word',
      paddingTop: '2px',
    }),
    title: css({
      margin: 0,
      fontSize: theme.typography.h6.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: theme.typography.h6.lineHeight,
      letterSpacing: 'inherit',
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    titleWithInfo: css({
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      maxWidth: '100%',
    }),
    titleText: css({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flexShrink: 1,
      minWidth: 0,
    }),
    description: css({
      display: '-webkit-box',
      WebkitLineClamp: 1,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      margin: 0,
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.colors.text.secondary,
    }),
    tagsContainer: css({
      paddingTop: theme.spacing(1),
    }),
    tagList: css({
      justifyContent: 'flex-start',
    }),
    actionsContainer: css({
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      marginTop: 'auto',
      paddingTop: theme.spacing(1),
    }),
    detailsContainer: css({
      width: '340px',
    }),
    detailValue: css({
      fontSize: theme.typography.bodySmall.fontSize,
      color: theme.colors.text.secondary,
    }),
    badgeContainer: css({
      position: 'absolute',
      bottom: theme.spacing(1),
      right: theme.spacing(1),
      zIndex: 1,
    }),
    dimmedImage: css({
      opacity: 0.3,
    }),
    placeholderText: css({
      color: theme.colors.text.disabled,
      fontStyle: 'italic',
    }),
  };
}

interface DashboardCardSkeletonProps {
  showCompatibilityBadge?: boolean;
}

const DashboardCardSkeleton: SkeletonComponent<DashboardCardSkeletonProps> = ({
  rootProps,
  showCompatibilityBadge,
}) => {
  const styles = useStyles2(getSkeletonStyles);
  return (
    <div className={styles.card} style={rootProps.style}>
      <Skeleton containerClassName={styles.thumbnail} height="100%" />
      <Skeleton height={22} width="70%" />
      <Skeleton height={19} width="90%" />
      {showCompatibilityBadge && <Skeleton height={33} width={100} />}
    </div>
  );
};

const getSkeletonStyles = (theme: GrafanaTheme2) => ({
  card: css({
    width: '350px',
    border: `1px solid ${theme.colors.border.strong}`,
    borderRadius: theme.shape.radius.default,
    overflow: 'hidden',
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    lineHeight: 1,
  }),
  thumbnail: css({
    display: 'block',
    width: '100%',
    aspectRatio: '16/9',
    borderRadius: theme.shape.radius.default,
    overflow: 'hidden',
    lineHeight: 1,
  }),
});

export const DashboardCard = attachSkeleton(DashboardCardComponent, DashboardCardSkeleton);
